import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../api';
import { urlEliminazione, messaggioEliminazione, avvisaConflitto } from '../../utils/eliminazione';
import Layout from '../../components/Layout';
import stili from '../../components/competenze/stili';
import ClienteRicerca from '../../components/competenze/ClienteRicerca';
import DicituraScelta from '../../components/competenze/DicituraScelta';
import FormClienteStudio from '../../components/competenze/FormClienteStudio';
import FormDicitura from '../../components/competenze/FormDicitura';
import ConflittoVersione, { eConflitto } from '../../components/ConflittoVersione';
import useAggiornamentoPeriodico from '../../hooks/useAggiornamentoPeriodico';
import useVersioniViste from '../../hooks/useVersioniViste';
import ModificataDaAltri from '../../components/ModificataDaAltri';
import { usePuoScrivere } from '../../utils/permessi';
import { formattaData } from '../../utils/date';
import CampiPratica from '../../components/competenze/CampiPratica';
import StampaParcellazione from '../../components/competenze/StampaParcellazione';
import AnteprimaSeriale from '../../components/competenze/AnteprimaSeriale';
import useElenco from '../../hooks/useElenco';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { formattaDurata, componiDescrizione } from '../../utils/durata';
import { durataDaOrari, parseEuro, formattaEuro } from '../../utils/importi';
import SelettorePeriodo from '../../components/SelettorePeriodo';
import './competenze.css';

// Data di oggi come AAAA-MM-GG, nel fuso del browser (nessun passaggio da UTC)
function oggi() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const meseDi = data => data.slice(0, 7);
function nomeMese(mese) {
  const [a, m] = mese.split('-').map(Number);
  return new Date(a, m - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
}

async function scarica(url, params, nomeFile) {
  const res = await api.get(url, { params, responseType: 'blob' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(res.data);
  link.download = nomeFile;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

export default function Competenze() {
  const puoScrivere = usePuoScrivere('competenze');
  // Link dallo Scadenziario: ?mese=AAAA-MM&evidenzia=ID mostra il mese e evidenzia la riga
  const [parametriUrl] = useSearchParams();
  const evidenziaId = Number(parametriUrl.get('evidenzia')) || null;
  const meseUrl = /^\d{4}-\d{2}$/.test(parametriUrl.get('mese') || '') ? parametriUrl.get('mese') : null;
  const [mese, setMese] = useState(meseUrl || meseDi(oggi()));
  const [tuttiMesi, setTuttiMesi] = useState(false);   // tutte le competenze dell'anno scelto
  const [filtri, setFiltri] = useState({ cliente: null, fatturante_id: '', eseguito_da: '', tipo_f24: '', da_verificare: false, automatica: '', cerca: '' });
  const [dati, setDati] = useState({ competenze: [], totale: 0, totale_minuti: 0 });
  const [fatturanti, setFatturanti] = useState([]);
  const [diciture, setDiciture] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [tariffe, setTariffe] = useState([]);
  const [inModifica, setInModifica] = useState(null);
  const [stampaParcellazione, setStampaParcellazione] = useState(false);
  const [nuovoCliente, setNuovoCliente] = useState(null);   // { testo, onCreato }
  const [nuovaDicitura, setNuovaDicitura] = useState(null); // { testo, onCreata }
  const [messaggio, setMessaggio] = useState('');
  const [errore, setErrore] = useState('');
  const [selezionate, setSelezionate] = useState([]);   // id delle competenze selezionate (solo righe visibili con i filtri attuali)
  const [esitoEliminazione, setEsitoEliminazione] = useState(null);   // { eliminate, non_eliminate }
  const [eliminazioneInCorso, setEliminazioneInCorso] = useState(false);
  const eliminazioneRef = useRef(false);
  const versioni = useVersioniViste();   // le eliminazioni mandano la versione che l'operatore ha visto davvero, non quella dell'ultimo aggiornamento automatico

  const parametri = useMemo(() => ({
    // Tutte le pratiche dell'anno: l'anno è l'unico parametro; altrimenti mese e anno scelti
    ...(tuttiMesi ? { anno: mese.slice(0, 4) } : { mese }),
    cliente_id: filtri.cliente?.id,
    fatturante_id: filtri.fatturante_id || undefined,
    eseguito_da: filtri.eseguito_da || undefined,
    tipo_f24: filtri.tipo_f24 || undefined,
    da_verificare: filtri.da_verificare ? '1' : undefined,
    automatica: filtri.automatica || undefined,
    cerca: filtri.cerca.trim() || undefined,
  }), [mese, tuttiMesi, filtri]);

  const carica = useCallback(async ({ silenzioso = false } = {}) => {
    try {
      const res = await api.get('/competenze', { params: parametri });
      versioni.aggiorna(res.data.competenze, { silenzioso });
      setDati(res.data);
      setSelezionate(sel => sel.filter(id => res.data.competenze.some(c => c.id === id)));   // restano selezionate solo le righe ancora visibili
    } catch (err) {
      setErrore(err.response?.data?.errore || 'Errore nel caricamento');
    }
  }, [parametri, versioni]);

  const caricaDiciture = useCallback(() => api.get('/competenze/diciture').then(r => setDiciture(r.data)).catch(() => {}), []);

  useEffect(() => { carica(); }, [carica]);
  useEffect(() => {
    api.get('/competenze/fatturanti').then(r => setFatturanti(r.data)).catch(() => {});
    api.get('/competenze/operatori').then(r => setOperatori(r.data)).catch(() => {});
    api.get('/competenze/tariffe-orarie').then(r => setTariffe(r.data)).catch(() => {});
    caricaDiciture();
  }, [caricaDiciture]);
  useAggiornamentoPeriodico(() => carica({ silenzioso: true }), { attivo: !inModifica && !nuovoCliente && !nuovaDicitura });

  const avvisa = (testo) => { setMessaggio(testo); setTimeout(() => setMessaggio(''), 4000); };

  const elimina = async (c) => {
    if (!window.confirm(`Spostare nel Cestino la prestazione "${c.descrizione}"?`)) return;
    try {
      await api.delete(urlEliminazione(`/competenze/${c.id}`, versioni.versioneVista(c)));
      window.dispatchEvent(new Event('cestino-aggiornato'));
      carica();
    } catch (err) {
      setErrore(avvisaConflitto(err, messaggioEliminazione(err)));
      if (eConflitto(err)) carica();   // qualcun altro l'ha modificata: si mostra la riga aggiornata
    }
  };

  // Eliminazione in blocco: conferma esplicita, poi una richiesta; le righe non eliminabili si elencano con il motivo
  const eliminaSelezionate = async () => {
    if (eliminazioneInCorso || eliminazioneRef.current || !selezionate.length) return;
    eliminazioneRef.current = true;
    if (!window.confirm(`Eliminare ${selezionate.length} ${selezionate.length === 1 ? 'competenza' : 'competenze'}? Finiranno nel Cestino e potranno essere ripristinate.`)) { eliminazioneRef.current = false; return; }
    setErrore('');
    setEliminazioneInCorso(true);
    try {
      const righe = dati.competenze.filter(c => selezionate.includes(c.id)).map(c => ({ id: c.id, version: versioni.versioneVista(c) }));
      const res = await api.post('/competenze/elimina-multiple', { righe });
      setEsitoEliminazione(res.data);
      setSelezionate([]);
      window.dispatchEvent(new Event('cestino-aggiornato'));
      carica();
    } catch (err) {
      setErrore(err.response?.data?.errore || 'Eliminazione non riuscita');
    } finally { eliminazioneRef.current = false; setEliminazioneInCorso(false); }
  };

  const tutteSelezionate = dati.competenze.length > 0 && selezionate.length === dati.competenze.length;

  const usaSuggerito = async (c) => {
    setErrore('');
    try {
      await api.post(`/competenze/${c.id}/importo-suggerito`, { version: c.version });
      avvisa('Importo suggerito salvato');
      carica();
    } catch (err) { setErrore(err.response?.data?.errore || 'Non è stato possibile salvare l\'importo'); carica(); }
  };

  const esporta = async (tipo) => {
    setErrore('');
    try {
      await scarica(`/competenze/export/${tipo}`, parametri, `parcellazione_${tuttiMesi ? mese.slice(0, 4) : mese}.${tipo === 'excel' ? 'xlsx' : 'pdf'}`);
    } catch {
      setErrore('Esportazione non riuscita');
    }
  };

  const tipiF24 = useElenco('tipo_f24');
  const attivi = fatturanti.filter(f => f.attivo);
  const aggiornaFiltro = (campo, valore) => setFiltri(f => ({ ...f, [campo]: valore }));

  return (
    <Layout>
      <div style={stili.topBar} className="noStampa">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>Parcellazione</h2>
          <SelettorePeriodo mese={mese} tuttiMesi={tuttiMesi} onMese={setMese} onTuttiMesi={setTuttiMesi} descrizione="competenze" />
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          <button onClick={() => esporta('excel')} style={stili.btnSecondary}>Esporta Excel</button>
          <button onClick={() => esporta('pdf')} style={stili.btnSecondary}>Esporta PDF</button>
          <button onClick={() => setStampaParcellazione(true)} style={stili.btnSecondary}>Stampa per parcellazione</button>
        </div>
      </div>

      {errore && <div style={stili.errore}>{errore}</div>}
      {messaggio && <div style={stiliPagina.ok}>{messaggio}</div>}

      {puoScrivere && (
        <PannelloInserimento
          diciture={diciture}
          operatori={operatori}
          tariffe={tariffe}
          fatturanti={attivi}
          dataIniziale={tuttiMesi || mese === meseDi(oggi()) ? oggi() : `${mese}-01`}
          onNuovoCliente={(testo, onCreato) => setNuovoCliente({ testo, onCreato })}
          onNuovaDicitura={(testo, onCreata) => setNuovaDicitura({ testo, onCreata })}
          onInserite={(n) => { avvisa(n === 1 ? 'Prestazione inserita' : `${n} prestazioni inserite`); carica(); }}
        />
      )}

      <div style={stiliPagina.filtri} className="noStampa">
        <div style={{ flex: '2 1 200px' }}>
          {filtri.cliente ? (
            <div style={stiliPagina.chip}>
              {filtri.cliente.ragione_sociale}
              <button onClick={() => aggiornaFiltro('cliente', null)} style={stiliPagina.chipX} aria-label="Togli filtro azienda">×</button>
            </div>
          ) : (
            <ClienteRicerca placeholder="Filtra per azienda…" onSeleziona={c => aggiornaFiltro('cliente', c)} />
          )}
        </div>
        <select value={filtri.fatturante_id} onChange={e => aggiornaFiltro('fatturante_id', e.target.value)} style={stiliPagina.select} aria-label="Fatturante">
          <option value="">Tutti i fatturanti</option>
          <option value="nessuno">Senza fatturante</option>
          {fatturanti.map(f => <option key={f.id} value={f.id}>{f.nome}{f.attivo ? '' : ' (non attivo)'}</option>)}
        </select>
        <select value={filtri.eseguito_da} onChange={e => aggiornaFiltro('eseguito_da', e.target.value)} style={stiliPagina.select} aria-label="Chi ha svolto">
          <option value="">Chiunque abbia svolto</option>
          {operatori.map(o => <option key={o.id} value={o.id}>{o.nome} {o.cognome}</option>)}
        </select>
        <select value={filtri.tipo_f24} onChange={e => aggiornaFiltro('tipo_f24', e.target.value)} style={stiliPagina.select} aria-label="Tipo F24">
          <option value="">Ogni tipo F24</option>
          {tipiF24.map(t => <option key={t.id} value={t.valore}>F24 {t.valore}</option>)}
        </select>
        <select value={filtri.automatica} onChange={e => aggiornaFiltro('automatica', e.target.value)} style={stiliPagina.select} aria-label="Automatiche GIS">
          <option value="">Automatiche e non</option>
          <option value="no">Senza automatiche GIS</option>
          <option value="si">Solo automatiche GIS</option>
        </select>
        <label style={stiliPagina.check}>
          <input type="checkbox" checked={filtri.da_verificare} onChange={e => aggiornaFiltro('da_verificare', e.target.checked)} /> Da verificare
        </label>
        <input type="search" placeholder="Cerca nel testo…" value={filtri.cerca} onChange={e => aggiornaFiltro('cerca', e.target.value)} style={{ ...stiliPagina.select, flex: '1 1 160px' }} />
      </div>

      {puoScrivere && selezionate.length > 0 && (
        <div style={stiliPagina.blocco} className="noStampa">
          <strong>{selezionate.length} selezionate</strong>
          <button onClick={eliminaSelezionate} disabled={eliminazioneInCorso} style={{ ...stili.btn, background: '#b91c1c', color: '#fff', fontWeight: 600 }}>
            {eliminazioneInCorso ? 'Eliminazione…' : 'Elimina selezionate'}
          </button>
          <button onClick={() => setSelezionate([])} style={stili.btnSmall}>Annulla selezione</button>
        </div>
      )}

      {esitoEliminazione && (
        <div role="status" className="noStampa" style={{ border: `1px solid ${esitoEliminazione.non_eliminate.length ? '#fdba74' : '#86efac'}`, background: esitoEliminazione.non_eliminate.length ? '#fff7ed' : '#ecfdf5', borderRadius: '8px', padding: '0.7rem 0.9rem', marginBottom: '0.8rem', fontSize: '0.88rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
            <strong>{esitoEliminazione.eliminate} {esitoEliminazione.eliminate === 1 ? 'eliminata' : 'eliminate'}{esitoEliminazione.non_eliminate.length ? `, ${esitoEliminazione.non_eliminate.length} non eliminate` : ''}</strong>
            <button onClick={() => setEsitoEliminazione(null)} style={stili.btnSmall}>Chiudi</button>
          </div>
          {esitoEliminazione.non_eliminate.length > 0 && (
            <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem', color: '#9a3412' }}>
              {esitoEliminazione.non_eliminate.map(x => <li key={x.id}><strong>{x.riferimento}</strong>: {x.motivo}</li>)}
            </ul>
          )}
        </div>
      )}

      <h3 className="soloStampa">Parcellazione · {tuttiMesi ? `anno ${mese.slice(0, 4)}` : nomeMese(mese)}</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ ...stili.table, tableLayout: 'fixed', minWidth: '980px' }} className="tabellaCompetenze tabellaCompatta">
          <thead>
            <tr>
              {puoScrivere && (
                <th style={{ ...stili.th, width: '3%' }} className="noStampa">
                  <input type="checkbox" aria-label="Seleziona tutte" checked={tutteSelezionate}
                    onChange={e => setSelezionate(e.target.checked ? dati.competenze.map(c => c.id) : [])} />
                </th>
              )}
              <th style={{ ...stili.th, width: '8%' }}>Data</th>
              <th style={{ ...stili.th, width: '12%' }}>Azienda</th>
              <th style={{ ...stili.th, width: puoScrivere ? '14%' : '17%' }}>Descrizione</th>
              <th style={{ ...stili.th, width: '5%' }}>Causale</th>
              <th style={{ ...stili.th, width: '8%' }}>Fatturante</th>
              <th style={{ ...stili.th, width: '8%' }}>Chi ha svolto</th>
              <th style={{ ...stili.th, width: '5%' }}>Dalle</th>
              <th style={{ ...stili.th, width: '5%' }}>Alle</th>
              <th style={{ ...stili.th, width: '5%' }}>Durata</th>
              <th style={{ ...stili.th, width: '6%' }}>Diritti</th>
              <th style={{ ...stili.th, width: '5%' }}>Bollo</th>
              <th style={{ ...stili.th, width: '8%' }}>Importo</th>
              {puoScrivere && <th style={{ ...stili.th, width: '8%' }} className="noStampa">Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {dati.competenze.map(c => (
              <tr key={c.id} style={{ ...stili.tr, ...(c.automatica_gis ? stiliPagina.rigaAuto : {}), ...(selezionate.includes(c.id) ? { background: '#F7F7EA' } : {}), ...(c.id === evidenziaId ? { background: '#F7F7EA', outline: '2px solid #D3D678' } : {}) }} ref={el => { if (el && c.id === evidenziaId) el.scrollIntoView({ block: 'center' }); }}>
                {puoScrivere && (
                  <td style={stili.td} className="noStampa">
                    <input type="checkbox" aria-label={`Seleziona prestazione ${c.id}`} checked={selezionate.includes(c.id)}
                      onChange={e => setSelezionate(s => (e.target.checked ? [...s, c.id] : s.filter(x => x !== c.id)))} />
                  </td>
                )}
                <td style={{ ...stili.td, whiteSpace: 'nowrap' }}>{formattaData(c.data)}</td>
                <td style={stili.td}>{c.cliente}</td>
                <td style={stili.td}>
                  {c.descrizione}
                  {versioni.cambiata(c) && <ModificataDaAltri onRiconosci={() => versioni.riconosci(c)} />}
                  {c.automatica_gis && <span style={stili.badgeAuto} title="Già addebitata in automatico da GIS: non va nel file per GIS">addebitata in automatico</span>}
                  {c.da_verificare && <span style={stiliPagina.badgeVerifica}>da verificare</span>}
                  {c.listino_stato === 'da_completare' && <div style={{ color: '#c2410c', fontSize: '0.76rem' }} title="Listino">importo da completare: {c.listino_motivo}</div>}
                  {c.nota_interna && <div style={{ color: '#666', fontSize: '0.78rem' }}>{c.nota_interna}</div>}
                </td>
                <td style={stili.td}>{c.causale_codice || <span style={{ color: '#c2410c' }} title="Nessuna causale per questo fatturante">—</span>}</td>
                <td style={stili.td}>{c.fatturante || <span style={{ color: '#999' }}>—</span>}</td>
                <td style={stili.td}>{c.eseguito_da_nome || <span style={{ color: '#999' }}>—</span>}</td>
                <td style={stili.td}>{c.dalle}</td>
                <td style={stili.td}>{c.alle}</td>
                <td style={stili.td}>{formattaDurata(c.minuti)}</td>
                <td style={stili.td}>{formattaEuro(c.diritti_segreteria)}</td>
                <td style={stili.td}>{formattaEuro(c.imposta_bollo)}</td>
                <td style={stili.td}>
                  {c.importo !== null && c.importo !== undefined && <strong>{formattaEuro(c.importo)}</strong>}
                  {(c.importo === null || c.importo === undefined) && c.importo_suggerito !== null && c.importo_suggerito !== undefined && (
                    <span title="Importo suggerito (ore × tariffa del ruolo): non è salvato finché non lo confermi">
                      <em style={{ color: '#666' }}>sugg. {formattaEuro(c.importo_suggerito)}</em>
                      {puoScrivere && <button onClick={() => usaSuggerito(c)} style={{ ...stili.btnSmall, marginTop: '0.2rem', marginRight: 0 }}>Usa</button>}
                    </span>
                  )}
                  {(c.importo === null || c.importo === undefined) && c.suggerimento === 'ruolo_mancante' && <span style={{ color: '#c2410c', fontSize: '0.76rem' }}>ruolo mancante</span>}
                  {(c.importo === null || c.importo === undefined) && c.suggerimento === 'tariffa_mancante' && <span style={{ color: '#c2410c', fontSize: '0.76rem' }}>tariffa mancante</span>}
                </td>
                {puoScrivere && (
                  <td style={stili.td} className="noStampa">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-start' }}>
                      <button onClick={() => setInModifica(c)} style={{ ...stili.btnSmall, marginRight: 0 }}>Modifica</button>
                      <button onClick={() => elimina(c)} style={stili.btnSmallDanger}>Elimina</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {dati.competenze.length === 0 && (
              <tr><td colSpan={puoScrivere ? 14 : 13} style={{ ...stili.td, textAlign: 'center', color: '#999', padding: '1.5rem' }}>Nessuna prestazione per questi filtri</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p style={stiliPagina.totali}>{dati.totale} righe · durata totale {formattaDurata(dati.totale_minuti) || '0:00'}</p>

      {inModifica && (
        <FormCompetenza
          competenza={inModifica}
          diciture={diciture}
          operatori={operatori}
          tariffe={tariffe}
          fatturanti={fatturanti}
          onChiudi={() => setInModifica(null)}
          onSalvato={() => { setInModifica(null); carica(); }}
        />
      )}
      {stampaParcellazione && <StampaParcellazione fatturanti={fatturanti} mese={mese} tuttiMesi={tuttiMesi} onChiudi={() => setStampaParcellazione(false)} />}
      {nuovoCliente && (
        <FormClienteStudio
          ragioneIniziale={nuovoCliente.testo}
          fatturanti={fatturanti}
          onChiudi={() => setNuovoCliente(null)}
          onSalvato={(c) => { nuovoCliente.onCreato(c); setNuovoCliente(null); }}
        />
      )}
      {nuovaDicitura && (
        <FormDicitura
          testoIniziale={nuovaDicitura.testo}
          fatturanti={fatturanti}
          onChiudi={() => setNuovaDicitura(null)}
          onSalvato={async (d) => { await caricaDiciture(); nuovaDicitura.onCreata(d); setNuovaDicitura(null); }}
        />
      )}
    </Layout>
  );
}

// Prezzo da listino per la dicitura scelta e i clienti indicati (anteprima: il server lo ricalcola al salvataggio)
function useListino(dicitura, idsClienti, data) {
  const [calcoli, setCalcoli] = useState([]);
  useEffect(() => {
    if (!dicitura?.listino_voce_id || !idsClienti) { setCalcoli([]); return undefined; }
    let attivo = true;
    api.get('/competenze/listino/calcola', { params: { dicitura_id: dicitura.id, cliente_ids: idsClienti, data } })
      .then(r => { if (attivo) setCalcoli(r.data); }).catch(() => { if (attivo) setCalcoli([]); });
    return () => { attivo = false; };
  }, [dicitura, idsClienti, data]);
  return calcoli;
}

function RiquadroListino({ dicitura, calcoli, nomi }) {
  if (!dicitura?.listino_voce_id) return null;
  return (
    <div style={{ border: '1px dashed #D3D678', background: '#fcfcf4', borderRadius: '6px', padding: '0.5rem 0.7rem', margin: '0 0 0.8rem', fontSize: '0.84rem' }}>
      <strong>Listino: {dicitura.listino_voce}</strong> <span style={{ color: '#777' }}>(IVA esclusa; si compila da solo in base al tipo soggetto dell'azienda e resta modificabile)</span>
      {calcoli.length === 0 && <div style={{ color: '#777' }}>Scegli l'azienda per vedere il prezzo.</div>}
      {calcoli.map(r => (
        <div key={r.cliente_id} style={{ color: r.stato === 'ok' ? '#166534' : '#c2410c' }}>
          {nomi[r.cliente_id] ? `${nomi[r.cliente_id]}: ` : ''}{r.stato === 'ok' ? `${formattaEuro(r.importo)} €` : `da completare: ${r.motivo}`}
        </div>
      ))}
    </div>
  );
}

// Riga di inserimento veloce: uno o più clienti, una competenza per ciascuno
function PannelloInserimento({ diciture, fatturanti, operatori, tariffe, dataIniziale, onNuovoCliente, onNuovaDicitura, onInserite }) {
  const { utente } = useAuth();
  const vuoto = { dicitura: null, variabili: {}, descrizione_libera: '', dalle: '', alle: '', diritti_segreteria: '', imposta_bollo: '', importo: '', da_verificare: false, nota_interna: '' };
  const [data, setData] = useState(dataIniziale);
  // Restano per la riga successiva: data, fatturante e chi ha svolto (il cursore torna sul cliente)
  const [fattComune, setFattComune] = useState(''); // '' = predefinito del cliente | id | 'nessuno'
  const [eseguito, setEseguito] = useState(utente?.id ? String(utente.id) : '');
  const [chiaveCliente, setChiaveCliente] = useState(0);
  const [clienti, setClienti] = useState([]); // [{ cliente, fatturante_id, origine }]
  const [campi, setCampi] = useState(vuoto);
  const [errore, setErrore] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);
  const occupato = useRef(false);   // doppio clic: lo stato React si aggiorna solo al ridisegno
  const [nomi, setNomi] = useState('');          // elenco di nomi incollato (uno per riga)
  const [incolla, setIncolla] = useState(false);
  const [anteprimaSeriale, setAnteprimaSeriale] = useState(null); // { anteprima, corpo }

  useEffect(() => { setData(dataIniziale); }, [dataIniziale]);

  const aggiungiCliente = async (cliente) => {
    if (clienti.some(r => r.cliente.id === cliente.id)) return;
    let proposta = { fatturante_id: null, origine: null };
    try { proposta = (await api.get('/competenze/fatturante-proposto', { params: { cliente_id: cliente.id } })).data; } catch { /* resta da scegliere */ }
    const proposto = proposta.fatturante_id || '';
    const iniziale = fattComune === 'nessuno' ? '' : (fattComune || proposto);
    setClienti(r => [...r, { cliente, fatturante_id: iniziale, origine: fattComune ? 'scelto' : proposta.origine, proposto }]);
  };

  // Cambiando il fatturante comune si aggiornano le righe dei clienti già aggiunti
  const cambiaFattComune = (v) => {
    setFattComune(v);
    setClienti(lista => lista.map(r => ({ ...r, fatturante_id: v === 'nessuno' ? '' : (v || r.proposto), origine: v ? 'scelto' : (r.proposto ? 'cliente' : null) })));
  };

  const idsClienti = clienti.map(r => r.cliente.id).join(',');
  const calcoli = useListino(campi.dicitura, idsClienti, data);
  const [importoAuto, setImportoAuto] = useState(false); // importo compilato dal listino (non scritto a mano)
  // Con un solo cliente l'importo del listino si compila da solo (resta modificabile); con più clienti ognuno prende il suo
  useEffect(() => {
    const unico = clienti.length === 1 ? calcoli[0] : null;
    if (unico && unico.stato === 'ok' && (campi.importo === '' || importoAuto)) {
      setCampi(c => ({ ...c, importo: formattaEuro(unico.importo) }));
      setImportoAuto(true);
    } else if (importoAuto && !(unico && unico.stato === 'ok')) {
      setCampi(c => ({ ...c, importo: '' }));
      setImportoAuto(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcoli]);
  const nomiClienti = Object.fromEntries(clienti.map(r => [r.cliente.id, r.cliente.ragione_sociale]));

  const cambiaCampo = (campo, valore) => {
    if (campo === 'importo') setImportoAuto(false);
    return campo === 'eseguito_da' ? setEseguito(valore) : setCampi(c => ({ ...c, [campo]: valore }));
  };
  const anteprima = componiDescrizione(campi.dicitura?.testo, campi.variabili, campi.descrizione_libera);

  const puoSerie = true;
  const righeNomi = nomi.split('\n').filter(n => n.trim()).length;
  const corpoComune = () => ({
    data,
    dicitura_id: campi.dicitura?.id || null,
    variabili: campi.variabili,
    descrizione_libera: campi.descrizione_libera,
    eseguito_da: Number(eseguito) || null,
    dalle: campi.dalle,
    alle: campi.alle,
    diritti_segreteria: campi.diritti_segreteria,
    imposta_bollo: campi.imposta_bollo,
    importo: campi.importo,
    da_verificare: campi.da_verificare,
    nota_interna: campi.nota_interna,
  });

  const chiudiConSuccesso = (n) => {
    setClienti([]); setNomi(''); setIncolla(false); setAnteprimaSeriale(null);
    setCampi(vuoto); setImportoAuto(false);
    setChiaveCliente(k => k + 1);
    onInserite(n);
  };
  // Fatturante di partenza di ogni riga dell'anteprima: quello già scelto nel pannello, altrimenti il predefinito del cliente
  const preimposta = (r) => {
    const scelto = clienti.find(x => r.cliente && x.cliente.id === r.cliente.id);
    if (scelto) return scelto.fatturante_id;
    if (fattComune === 'nessuno') return '';
    return fattComune || undefined;
  };

  const salva = async (e) => {
    e.preventDefault();
    setErrore('');
    if (!clienti.length && !righeNomi) return setErrore("Scegli almeno un'azienda dall'anagrafica o incolla un elenco di nomi");
    const orari = durataDaOrari(campi.dalle, campi.alle);
    if (orari.errore) return setErrore(orari.errore);
    for (const [campo, nome] of [['diritti_segreteria', 'Diritti di segreteria'], ['imposta_bollo', 'Imposta di bollo'], ['importo', 'Importo']]) {
      const p = parseEuro(campi[campo], nome);
      if (p.errore) return setErrore(p.errore);
    }
    if (!anteprima) return setErrore('Scegli una dicitura o scrivi una descrizione');
    if (occupato.current) return;
    occupato.current = true;
    setSalvataggio(true);
    try {
      // Più clienti, o un elenco di nomi incollato: prima l'anteprima, si crea solo dopo la conferma
      if (clienti.length > 1 || righeNomi > 0) {
        const corpo = corpoComune();
        const res = await api.post('/competenze/seriale/anteprima', { ...corpo, selezionati: clienti.map(r => r.cliente.id), nomi });
        setAnteprimaSeriale({ anteprima: res.data, corpo });
        return;
      }
      const res = await api.post('/competenze', {
        ...corpoComune(),
        clienti: clienti.map(r => ({ cliente_id: r.cliente.id, fatturante_id: Number(r.fatturante_id) || null, senza_fatturante: !r.fatturante_id })),
      });
      chiudiConSuccesso(res.data.competenze.length);
    } catch (err) {
      setErrore(err.response?.data?.errore || 'Inserimento non riuscito');
    } finally {
      occupato.current = false; setSalvataggio(false);
    }
  };

  const causaleDi = fatturanteId => campi.dicitura?.causali?.[fatturanteId];

  return (
    <form onSubmit={salva} style={stiliPagina.pannello} className="noStampa">
      <div style={stiliPagina.griglia}>
        <div>
          <label style={stili.label}>Data</label>
          <input type="date" value={data} onChange={e => setData(e.target.value)} required style={stili.input} />
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <label style={stili.label}>Aziende (una o più)</label>
          <ClienteRicerca
            key={chiaveCliente}
            autoFocus
            placeholder="Cerca e aggiungi azienda…"
            onSeleziona={aggiungiCliente}
            onNuovo={(testo) => onNuovoCliente(testo, aggiungiCliente)}
          />
          {puoSerie && (
            <button type="button" onClick={() => setIncolla(v => !v)} style={{ ...stili.btnSmall, marginTop: '0.3rem' }}>
              {incolla ? 'Nascondi elenco di nomi' : 'Incolla un elenco di nomi'}{righeNomi > 0 && !incolla ? ` (${righeNomi})` : ''}
            </button>
          )}
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <label style={stili.label}>Dicitura</label>
          <DicituraScelta
            diciture={diciture}
            dicituraId={campi.dicitura?.id}
            variabili={campi.variabili}
            onCambia={d => setCampi(c => ({ ...c, dicitura: d, variabili: {} }))}
            onCambiaVariabili={v => setCampi(c => ({ ...c, variabili: v }))}
            onNuova={(testo) => onNuovaDicitura(testo, d => setCampi(c => ({ ...c, dicitura: d, variabili: {} })))}
          />
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <label style={stili.label}>Descrizione libera</label>
          <input value={campi.descrizione_libera} onChange={e => setCampi(c => ({ ...c, descrizione_libera: e.target.value }))} style={stili.input} placeholder="Facoltativa se c'è la dicitura" />
        </div>
        <div>
          <label style={stili.label}>Fatturante</label>
          <select value={fattComune} onChange={e => cambiaFattComune(e.target.value)} style={stili.input} aria-label="Fatturante delle righe">
            <option value="">Predefinito dell'azienda</option>
            <option value="nessuno">Nessun fatturante</option>
            {fatturanti.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <label style={stili.label}>Nota interna</label>
          <input value={campi.nota_interna} onChange={e => setCampi(c => ({ ...c, nota_interna: e.target.value }))} style={stili.input} placeholder="Note interne" />
        </div>
        <label style={{ ...stiliPagina.check, alignSelf: 'end' }}>
          <input type="checkbox" checked={campi.da_verificare} onChange={e => setCampi(c => ({ ...c, da_verificare: e.target.checked }))} /> Da verificare
        </label>
      </div>

      <div style={{ marginTop: '0.8rem' }}>
        <RiquadroListino dicitura={campi.dicitura} calcoli={calcoli} nomi={nomiClienti} />
        <CampiPratica valori={{ ...campi, eseguito_da: eseguito }} onCambia={cambiaCampo} operatori={operatori} tariffe={tariffe} />
      </div>

      {incolla && (
        <div style={{ marginTop: '0.8rem' }}>
          <label style={stili.label} htmlFor="elenco-nomi">Elenco di nomi, uno per riga ({righeNomi} {righeNomi === 1 ? 'riga' : 'righe'})</label>
          <textarea id="elenco-nomi" value={nomi} onChange={e => setNomi(e.target.value)} rows={6} style={{ ...stili.input, fontFamily: 'inherit' }}
            placeholder={'Rossi Costruzioni Srl\nBianchi Mario\n…'} />
          <small style={{ color: '#777' }}>I nomi vengono confrontati con l'archivio nell'anteprima: non viene creato nulla finché non confermi. Massimo 300 aziende per operazione.</small>
        </div>
      )}

      <div style={stiliPagina.riquadroInserimento} className="nuovaCompetenza" role="group" aria-label="Nuova competenza">
      <h3 style={stiliPagina.titoloRiquadro}>Nuova competenza</h3>
      {clienti.length > 0 && (
        <div style={{ marginTop: '0.4rem' }}>
          {clienti.map((r, i) => (
            <div key={r.cliente.id} style={stiliPagina.rigaCliente}>
              <span style={{ flex: '2 1 200px', fontWeight: 600 }}>{r.cliente.ragione_sociale}</span>
              <select
                value={r.fatturante_id}
                onChange={e => setClienti(lista => lista.map((x, j) => (j === i ? { ...x, fatturante_id: e.target.value, origine: 'scelto' } : x)))}
                style={{ ...stili.input, flex: '1 1 160px', width: 'auto' }}
                aria-label={`Fatturante per ${r.cliente.ragione_sociale}`}
              >
                <option value="">Nessun fatturante</option>
                {fatturanti.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
              </select>
              <small style={{ flex: '1 1 150px', color: '#777' }}>
                {r.origine === 'cliente' && 'predefinito dell\'azienda'}
                {r.fatturante_id && campi.dicitura && (causaleDi(r.fatturante_id)
                  ? ` · causale ${causaleDi(r.fatturante_id)}` : ' · nessuna causale per questo fatturante')}
                {r.fatturante_id && !r.cliente.codici_gis?.[r.fatturante_id] && ` · manca il codice azienda parcellazione di ${fatturanti.find(f => f.id === r.fatturante_id)?.nome || 'questo fatturante'}`}
              </small>
              <button type="button" onClick={() => setClienti(lista => lista.filter((_, j) => j !== i))} style={stili.btnSmallDanger} aria-label={`Togli ${r.cliente.ragione_sociale}`}>Togli</button>
            </div>
          ))}
        </div>
      )}

      {errore && <div style={{ ...stili.errore, marginTop: '0.6rem' }}>{errore}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.7rem', gap: '1rem', flexWrap: 'wrap' }}>
        <small style={{ color: '#555' }}>
          {anteprima ? <>Testo della competenza: <strong>{anteprima}</strong>{campi.dicitura?.automatica_gis && <span style={stili.badgeAuto}>addebitata in automatico</span>}</> : ' '}
        </small>
        <button type="submit" disabled={salvataggio} style={stili.btn}>
          {salvataggio ? 'Salvataggio…' : (clienti.length > 1 || righeNomi > 0) ? `Anteprima (${clienti.length + righeNomi} aziende)` : 'Inserisci'}
        </button>
      </div>
      </div>
      {anteprimaSeriale && (
        <AnteprimaSeriale
          anteprima={anteprimaSeriale.anteprima}
          corpo={anteprimaSeriale.corpo}
          fatturanti={fatturanti}
          preimposta={preimposta}
          onAnnulla={() => setAnteprimaSeriale(null)}
          onCreate={chiudiConSuccesso}
        />
      )}
    </form>
  );
}

// Modifica di una competenza, con controllo di versione
function FormCompetenza({ competenza, diciture, fatturanti, operatori, tariffe, onChiudi, onSalvato }) {
  const daCompetenza = c => ({
    data: c.data,
    cliente: { id: c.cliente_id, ragione_sociale: c.cliente },
    fatturante_id: c.fatturante_id || '',
    dicitura: diciture.find(d => d.id === c.dicitura_id) || (c.dicitura_id ? { id: c.dicitura_id, testo: c.dicitura_testo, variabili: [], causali: {} } : null),
    variabili: c.variabili || {},
    descrizione_libera: c.descrizione_libera || '',
    eseguito_da: c.eseguito_da ? String(c.eseguito_da) : '',
    dalle: c.dalle || '',
    alle: c.alle || '',
    diritti_segreteria: formattaEuro(c.diritti_segreteria),
    imposta_bollo: formattaEuro(c.imposta_bollo),
    importo: formattaEuro(c.importo),
    da_verificare: c.da_verificare,
    nota_interna: c.nota_interna || '',
    version: c.version,
  });
  const [form, setForm] = useState(daCompetenza(competenza));
  const [errore, setErrore] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);
  const occupato = useRef(false);   // doppio clic: lo stato React si aggiorna solo al ridisegno
  const [conflitto, setConflitto] = useState(null);
  const [cambiato, setCambiato] = useState(false); // dicitura o cliente cambiati in questa modifica
  const calcoli = useListino(form.dicitura, form.cliente?.id ? String(form.cliente.id) : '', form.data);
  // Cambiando dicitura o cliente, con l'importo vuoto si compila dal listino; un importo già scritto non si tocca
  useEffect(() => {
    if (cambiato && calcoli[0]?.stato === 'ok' && form.importo === '') setForm(f => ({ ...f, importo: formattaEuro(calcoli[0].importo) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcoli, cambiato]);
  const anteprima = componiDescrizione(form.dicitura?.testo, form.variabili, form.descrizione_libera);

  const salva = async (version) => {
    setErrore('');
    if (occupato.current) return;
    occupato.current = true;
    setSalvataggio(true);
    try {
      await api.put(`/competenze/${competenza.id}`, {
        data: form.data,
        cliente_id: form.cliente?.id,
        fatturante_id: Number(form.fatturante_id) || null,
        dicitura_id: form.dicitura?.id || null,
        variabili: form.variabili,
        descrizione_libera: form.descrizione_libera,
        eseguito_da: Number(form.eseguito_da) || null,
        dalle: form.dalle,
        alle: form.alle,
        diritti_segreteria: form.diritti_segreteria,
        imposta_bollo: form.imposta_bollo,
        importo: form.importo,
        da_verificare: form.da_verificare,
        nota_interna: form.nota_interna,
        version,
      });
      setConflitto(null);
      onSalvato();
    } catch (err) {
      if (eConflitto(err)) setConflitto(err.response.data);
      else setErrore(err.response?.data?.errore || 'Errore nel salvataggio');
    } finally {
      occupato.current = false; setSalvataggio(false);
    }
  };

  const causale = form.dicitura?.causali?.[form.fatturante_id];

  return (
    <div style={stili.overlay}>
      <div style={{ ...stili.modal, maxWidth: '760px' }}>
        <h3 style={{ marginTop: 0 }}>Modifica prestazione</h3>
        <form onSubmit={e => { e.preventDefault(); salva(form.version); }}>
          {errore && <div style={stili.errore}>{errore}</div>}
          <div style={stili.row}>
            <div style={stili.campo}>
              <label style={stili.label}>Data</label>
              <input type="date" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} required style={stili.input} />
            </div>
            <div style={{ ...stili.campo, flex: 2 }}>
              <label style={stili.label}>Azienda</label>
              <ClienteRicerca valore={form.cliente} onSeleziona={c => { setCambiato(true); setForm(f => ({ ...f, cliente: c })); }} />
            </div>
          </div>
          <div style={stili.campo}>
            <label style={stili.label}>Dicitura</label>
            <DicituraScelta
              diciture={diciture}
              dicituraId={form.dicitura?.id}
              variabili={form.variabili}
              onCambia={d => { setCambiato(true); setForm(f => ({ ...f, dicitura: d, variabili: {} })); }}
              onCambiaVariabili={v => setForm(f => ({ ...f, variabili: v }))}
            />
          </div>
          <div style={stili.campo}>
            <label style={stili.label}>Descrizione libera</label>
            <input value={form.descrizione_libera} onChange={e => setForm(f => ({ ...f, descrizione_libera: e.target.value }))} style={stili.input} />
            <small style={{ color: '#555' }}>Testo: <strong>{anteprima || '—'}</strong></small>
          </div>
          <div style={stili.row}>
            <div style={stili.campo}>
              <label style={stili.label}>Fatturante</label>
              <select value={form.fatturante_id} onChange={e => setForm(f => ({ ...f, fatturante_id: e.target.value }))} style={stili.input}>
                <option value="">Nessun fatturante</option>
                {fatturanti.filter(f => f.attivo || f.id === competenza.fatturante_id).map(f => <option key={f.id} value={f.id}>{f.nome}{f.attivo ? '' : ' (non attivo)'}</option>)}
              </select>
              {form.dicitura && <small style={{ color: causale ? '#555' : '#c2410c' }}>{causale ? `Causale ${causale}` : 'Nessuna causale per questo fatturante'}</small>}
            </div>
          </div>
          <RiquadroListino dicitura={form.dicitura} calcoli={calcoli} nomi={{}} />
          <CampiPratica valori={form} onCambia={(campo, valore) => setForm(f => ({ ...f, [campo]: valore }))} operatori={operatori} tariffe={tariffe}
            minutiRegistrati={!competenza.dalle ? competenza.minuti : null} esecutoreAttuale={competenza.eseguito_da} />
          <div style={stili.campo}>
            <label style={stili.label}>Nota interna</label>
            <input value={form.nota_interna} onChange={e => setForm(f => ({ ...f, nota_interna: e.target.value }))} style={stili.input} />
          </div>
          <label style={stiliPagina.check}>
            <input type="checkbox" checked={form.da_verificare} onChange={e => setForm(f => ({ ...f, da_verificare: e.target.checked }))} /> Da verificare
          </label>
          <div style={stili.azioni}>
            <button type="button" onClick={onChiudi} style={stili.btnSecondary}>Annulla</button>
            <button type="submit" disabled={salvataggio} style={stili.btn}>{salvataggio ? 'Salvataggio…' : 'Salva'}</button>
          </div>
        </form>
      </div>
      {conflitto && (
        <ConflittoVersione
          conflitto={conflitto}
          inCorso={salvataggio}
          onRicarica={() => { setForm(daCompetenza(conflitto.attuale)); setConflitto(null); }}
          onSovrascrivi={() => salva(conflitto.attuale.version)}
        />
      )}
    </div>
  );
}

const stiliPagina = {
  ok: { background: '#ecfdf5', color: '#166534', padding: '0.6rem', borderRadius: '6px', marginBottom: '0.8rem', fontSize: '0.88rem' },
  pannello: { background: 'white', borderRadius: '10px', padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '1rem', borderTop: '3px solid #D3D678' },
  // Riquadro della parte in basso della maschera (righe delle aziende, testo e "Inserisci"): bordo e sfondo propri
  riquadroInserimento: { background: '#FBFBF3', border: '2px solid #D3D678', borderRadius: '10px', padding: '1rem 1.2rem', marginTop: '1rem' },
  titoloRiquadro: { margin: '0 0 0.6rem', fontSize: '1.15rem', color: '#232323' },
  griglia: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.7rem' },
  rigaCliente: { display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0', borderBottom: '1px solid #f2f2f2', flexWrap: 'wrap' },
  filtri: { display: 'flex', gap: '0.5rem', marginBottom: '0.8rem', flexWrap: 'wrap', alignItems: 'center' },
  select: { padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.88rem', background: 'white' },
  check: { display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.88rem', whiteSpace: 'nowrap' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#F7F7EA', border: '1px solid #D3D678', borderRadius: '999px', padding: '0.35rem 0.4rem 0.35rem 0.8rem', fontSize: '0.88rem' },
  chipX: { border: 'none', background: 'none', cursor: 'pointer', fontSize: '1rem', color: '#555' },
  blocco: { display: 'flex', alignItems: 'center', gap: '0.6rem', background: '#232323', color: 'white', padding: '0.6rem 0.9rem', borderRadius: '8px', marginBottom: '0.8rem', flexWrap: 'wrap' },
  rigaAuto: { background: '#fbfbf3' },
  statoOk: { fontSize: '0.75rem', background: '#ecfdf5', color: '#166534', padding: '0.1rem 0.45rem', borderRadius: '4px', whiteSpace: 'nowrap' },
  statoDaFare: { fontSize: '0.75rem', background: '#fff7ed', color: '#9a3412', padding: '0.1rem 0.45rem', borderRadius: '4px', whiteSpace: 'nowrap' },
  badgeCompletare: { display: 'inline-block', fontSize: '0.68rem', background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74', padding: '0.05rem 0.35rem', borderRadius: '4px', marginLeft: '0.35rem', whiteSpace: 'nowrap' },
  badgeVerifica: { display: 'inline-block', fontSize: '0.68rem', background: '#fef2f2', color: '#b91c1c', padding: '0.05rem 0.35rem', borderRadius: '4px', marginLeft: '0.35rem' },
  totali: { color: '#555', fontSize: '0.88rem', marginTop: '0.6rem' },
};
